import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { XIcon } from "lucide-react";
import { useTranslate } from "ra-core";
import {
  createContext,
  useContext,
  useMemo,
  useRef,
  type ComponentProps,
  type ReactNode,
} from "react";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTapThroughGuard } from "@/lib/tapThroughGuard";
import { cn } from "@/lib/utils";
import "@/styles/page-dialog.css";

import { usePageSheetDrag } from "../layout/usePageSheetDrag";
import { useMobileFormGuard } from "./useMobileFormGuard";

/**
 * A dialog that stays the classic centred modal from `md` up and becomes a
 * full-screen liquid-glass page sheet on a phone (styles/page-dialog.css):
 * safe areas, a glass header with a round x where the top bar's avatar sits,
 * a spring slide up / down, swipe down (header or content pulled from its
 * top) to dismiss, Back closes it (useMobileFormGuard), and the base-ui
 * dialog keeps the focus trap, Escape and the focus return.
 *
 * Drop-in for `Dialog` / `DialogContent`: the desktop classes passed to
 * `ResponsiveDialogContent` are kept as they are (the mobile layout is a
 * `max-md` override in CSS, so there is no flash while `useIsMobile`
 * resolves).
 */

interface ResponsiveDialogContextValue {
  close: () => void;
}

const ResponsiveDialogContext =
  createContext<ResponsiveDialogContextValue | null>(null);

export interface ResponsiveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Routed dialogs (the URL already has its own history entry): false. */
  history?: boolean;
  children: ReactNode;
}

export const ResponsiveDialog = ({
  open,
  onOpenChange,
  history = true,
  children,
}: ResponsiveDialogProps) => {
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  useMobileFormGuard(open, () => onOpenChangeRef.current(false), { history });
  useTapThroughGuard(open);
  const context = useMemo(
    () => ({ close: () => onOpenChangeRef.current(false) }),
    [],
  );

  return (
    <ResponsiveDialogContext.Provider value={context}>
      <Dialog open={open} onOpenChange={(next) => onOpenChange(next)}>
        {children}
      </Dialog>
    </ResponsiveDialogContext.Provider>
  );
};

export type ResponsiveDialogContentProps = ComponentProps<
  typeof DialogContent
> & {
  /** Title centred in the phone header, next to the x (optional). */
  mobileTitle?: ReactNode;
};

export const ResponsiveDialogContent = ({
  className,
  children,
  mobileTitle,
  ...props
}: ResponsiveDialogContentProps) => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const context = useContext(ResponsiveDialogContext);
  const popupRef = useRef<HTMLDivElement>(null);
  const noBackdrop = useRef<HTMLElement>(null);
  const dragHandlers = usePageSheetDrag({
    sheetRef: popupRef,
    backdropRef: noBackdrop,
    scrollRef: popupRef,
    enabled: isMobile && context != null,
    onDismiss: () => context?.close(),
  });

  return (
    <DialogContent
      ref={popupRef}
      data-page-dialog=""
      className={cn("page-dialog", className)}
      {...props}
    >
      <div className="page-dialog-header md:hidden" {...dragHandlers}>
        <DialogPrimitive.Close
          data-slot="page-dialog-close"
          className="page-dialog-close liquid-glass glass-press flex size-[var(--mobile-control)] shrink-0 items-center justify-center rounded-full outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          aria-label={translate("ra.action.close", { _: "Close" })}
        >
          <XIcon className="size-5" strokeWidth={2} />
        </DialogPrimitive.Close>
        {mobileTitle ? (
          <span className="page-dialog-title">{mobileTitle}</span>
        ) : null}
      </div>
      {children}
    </DialogContent>
  );
};
